import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, inject, input, output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { ConnectorImageDialogComponent } from '../connector-image-dialog/connector-image-dialog.component';
import {
  DynamicPriceChartDialogComponent,
  DynamicPriceChartDialogData,
} from './dialogs/dynamic-price-chart-dialog/dynamic-price-chart-dialog.component';
import { ChargingStationLocation, ConnectorStatus, FirestoreConnectorInfo } from '../charging-station-location-model';
import {
  ConnectorPricePolicyDialogComponent,
  ConnectorPricePolicyDialogData,
} from '../connector-price-policy-dialog/connector-price-policy-dialog.component';
import { Subscription } from 'rxjs';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { RouterLink } from '@angular/router';
import { RequiresFeatureRoleDirective } from '../../../shared/directives/requires-feature-role.directive';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatBadge } from '@angular/material/badge';
import { MatTooltip } from '@angular/material/tooltip';
import { DecimalPipe } from '@angular/common';
import { Permission } from 'src/app/shared/enums/permission.enum';
import { PriceType } from '../charging-station-location-model';
import { PricePolicyType } from '../../prices/price-policies/interfaces/price-policy';
import { LocationDetailsConnector } from '../charging-station-location-details/charging-station-locations-details.model';

@Component({
  selector: 'adm-connector-list',
  templateUrl: './connector-list.component.html',
  styleUrls: ['./connector-list.component.scss'],
  imports: [
    RouterLink,
    RequiresFeatureRoleDirective,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatBadge,
    MatTooltip,
    DecimalPipe,
  ],
})
export class ConnectorListComponent implements OnInit, OnDestroy {
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private firestoreService = inject(FirestoreService);

  connectors = input.required<LocationDetailsConnector[]>();
  sortedConnectors = computed(() => [...this.connectors()].sort((a, b) => (a.stationNumber ?? Infinity) - (b.stationNumber ?? Infinity)));
  location = input.required<ChargingStationLocation>();
  organizationId = input.required<number>();
  selectedPricePolicyId = input<number | undefined>();
  reloadRequested = output<void>();
  connectorStatusChanged = this.firestoreService.connectorStatusChanged$();

  PriceType = PriceType;
  Permission = Permission;

  public roles = this.environmentLoaderService.virtualRoles;
  public connectorStatusEnum: typeof ConnectorStatus = ConnectorStatus;
  public connectorStatusChangedSubscription: Subscription | null = null;

  public displayColumns = [
    'status',
    'stationNumber',
    'csid',
    'maxPower',
    'pricePerKwh',
    'pricePolicyName',
    'accessGroups',
    'terminals',
    'image',
    'edit',
  ];

  ngOnDestroy(): void {
    this.connectorStatusChangedSubscription?.unsubscribe();
    this.firestoreService.tearDownFireStoreConnectorSubscriptions();
  }

  ngOnInit(): void {
    this.firestoreService.setupFireStoreConnectorSubscription(this.location().identifier);

    this.connectorStatusChangedSubscription = this.connectorStatusChanged.subscribe((firestoreConnectors: FirestoreConnectorInfo[]) => {
      let hasConnectorChanges = false;

      firestoreConnectors.forEach((firestoreConnector: FirestoreConnectorInfo) => {
        const connector = this.connectors().find(
          c => c.stationIdentifier === firestoreConnector.chargingStationIdentifier && c.connectorId === firestoreConnector.connectorNumber
        );
        if (connector) {
          connector.firestoreConnectorInfo.next(firestoreConnector);
          hasConnectorChanges = true;
        }
      });

      if (hasConnectorChanges) {
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  public showImage(connector: LocationDetailsConnector) {
    this.dialog.open(ConnectorImageDialogComponent, {
      data: {
        header: 'Billede af ladepunkt',
        connector: connector,
        src: this.getConnectorImageTimestamped(connector.identifier),
        editMode: false,
      },
      disableClose: true,
      width: '1024px',
    });
  }

  public openEditImage(connector: LocationDetailsConnector) {
    const dialogRef = this.dialog.open(ConnectorImageDialogComponent, {
      data: {
        header: 'Tilføj/rediger billede',
        connector: connector,
        src: this.getConnectorImageTimestamped(connector.identifier),
        editMode: true,
      },
      disableClose: true,
      width: '1024px',
    });

    dialogRef.afterClosed().subscribe(updated => {
      if (updated) {
        connector.connectorThumbnail = this.getConnectorThumbnailTimestamped(connector.identifier);
        connector.isThumbnailHidden = false;
        this.reloadRequested.emit();
      }
    });
  }

  public openDynamicPriceChartDialog(connector: LocationDetailsConnector) {
    this.dialog.open(DynamicPriceChartDialogComponent, {
      data: {
        connector,
        locationAddress: this.location()?.address.fullAddress,
        stationIdentifier: connector.stationIdentifier ?? '',
      } as DynamicPriceChartDialogData,
      width: '90vw',
      maxWidth: '1280px',
    });
  }

  public openEditPricePolicy(connector: LocationDetailsConnector) {
    const pricePolicyType = this.location().hasOkPowerMeter ? PricePolicyType.FixedWithOkMeter : PricePolicyType.FixedWithoutOkMeter;

    const dialogRef = this.dialog.open(ConnectorPricePolicyDialogComponent, {
      data: {
        organizationId: this.organizationId(),
        connector: connector,
        pricePolicyType,
        canHaveDynamicPricePolicy: this.location().canHaveDynamicPricePolicy,
      } satisfies ConnectorPricePolicyDialogData,
      disableClose: true,
      width: '700px',
    });

    dialogRef.afterClosed().subscribe(updated => {
      if (updated) {
        this.reloadRequested.emit();
      }
    });
  }

  public getCsManagementChargingStationsUrl(stationIdentifier?: string | null): string {
    const baseUrl = this.environmentLoaderService.config['csmanagement_url'];

    //In case someone added / at the end of the url in helm chart setup!
    const normalizedBaseUrl = String(baseUrl).replace(/\/+$/, '');
    const search = encodeURIComponent(stationIdentifier ?? '');

    return `${normalizedBaseUrl}/charging-stations?search=${search}`;
  }

  private getConnectorThumbnailTimestamped(connectorIdentifier: string): string {
    return `${
      this.environmentLoaderService.config['serviceBaseUrl']
    }/api/v1/admin/ConnectorPicture/${connectorIdentifier}/thumbnail?${new Date().getTime()}`;
  }

  private getConnectorImageTimestamped(connectorIdentifier: string): string {
    return `${
      this.environmentLoaderService.config['serviceBaseUrl']
    }/api/v1/admin/ConnectorPicture/${connectorIdentifier}?${new Date().getTime()}`;
  }
}
